import React from 'react';
import { Link } from 'react-router-dom';
import { Twitter, Linkedin, Youtube, Github, Instagram } from 'lucide-react';
import { Input } from './ui/input';
import BLKLogo from './BLKLogo';
import { footerColumns } from '../mock';
import { useToast } from '../hooks/use-toast';

// Maps each footer label to a working destination.
const linkMap = {
  RapidReps: { type: 'route', to: '/rapidreps-support' },
  Apptonomy: { type: 'hash', href: '#products' },
  HonestPay: { type: 'hash', href: '#products' },
  HaulAzz: { type: 'hash', href: '#products' },
  HirePower: { type: 'hash', href: '#products' },
  GRNBook: { type: 'hash', href: '#products' },
  RayOfHope: { type: 'hash', href: '#products' },
  Services: { type: 'hash', href: '#studio' },
  'Our Process': { type: 'hash', href: '#studio' },
  'Case Studies': { type: 'hash', href: '#studio' },
  Pricing: { type: 'hash', href: '#pricing' },
  'Book a Call': { type: 'action' },
  Blog: { type: 'hash', href: '#about' },
  Templates: { type: 'hash', href: '#about' },
  Documentation: { type: 'hash', href: '#about' },
  'Help Center': { type: 'route', to: '/rapidreps-support' },
  Community: { type: 'hash', href: '#about' },
  'About Us': { type: 'hash', href: '#about' },
  'Our Mission': { type: 'hash', href: '#mission' },
  Careers: { type: 'action' },
  'Contact Us': { type: 'action' },
  Partners: { type: 'action' },
};

const Footer = ({ onGetStarted }) => {
  const { toast } = useToast();
  const linkCls = 'text-gray-400 text-[12.5px] hover:text-white transition-colors text-left';

  const FooterLink = ({ label }) => {
    const d = linkMap[label] || { type: 'hash', href: '#products' };
    if (d.type === 'route') return <Link to={d.to} className={linkCls}>{label}</Link>;
    if (d.type === 'action')
      return (
        <button type="button" onClick={onGetStarted} className={`${linkCls} bg-transparent`}>
          {label}
        </button>
      );
    return <a href={d.href} className={linkCls}>{label}</a>;
  };

  const handleSubscribe = () => {
    toast({ title: 'Subscribed', description: 'Thanks — you’re on the list.' });
  };

  return (
    <footer className="relative border-t border-white/5 bg-[#050508]/80 pt-14 pb-6">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-8">
          {/* Logo column */}
          <div className="col-span-2">
            <BLKLogo />
            <p className="text-gray-500 text-[12.5px] mt-4 max-w-[240px] leading-relaxed">
              Building the future with AI, software, and innovation. Our mission is to empower businesses and creators through powerful technology.
            </p>
            <div className="flex gap-3 mt-5">
              {[Twitter, Linkedin, Youtube, Github, Instagram].map((Icon, i) => (
                <a
                  key={i}
                  href="#"
                  className="w-8 h-8 rounded-md bg-white/5 border border-white/5 flex items-center justify-center text-gray-400 hover:text-white hover:border-[#a855f7]/40 hover:bg-[#a855f7]/10 transition-colors"
                  aria-label="social"
                >
                  <Icon className="w-3.5 h-3.5" />
                </a>
              ))}
            </div>
          </div>

          {/* Columns */}
          {Object.entries(footerColumns).map(([col, links]) => (
            <div key={col}>
              <div className="text-white font-semibold text-[13.5px] mb-4">{col}</div>
              <ul className="space-y-2.5">
                {links.map((l) => (
                  <li key={l}>
                    <FooterLink label={l} />
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Newsletter */}
          <div className="col-span-2 lg:col-span-1">
            <div className="text-white font-semibold text-[13.5px] mb-4">Stay Updated</div>
            <p className="text-gray-400 text-[11.5px] mb-3 leading-relaxed">
              Get the latest updates, tips, and exclusive offers from BLKPixel.
            </p>
            <div className="relative">
              <Input
                type="email"
                placeholder="Enter your email"
                className="h-10 pr-10 bg-white/5 border-white/10 text-white text-[12.5px] placeholder:text-gray-500 focus-visible:ring-1 focus-visible:ring-[#a855f7]"
              />
              <button
                type="button"
                onClick={handleSubscribe}
                aria-label="Subscribe"
                className="absolute right-1 top-1 h-8 w-8 rounded bg-gradient-to-r from-[#7c3aed] to-[#4f46e5] flex items-center justify-center text-white"
              >
                →
              </button>
            </div>
            <p className="text-gray-500 text-[10px] mt-2">We respect your privacy. Unsubscribe anytime.</p>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/5 flex flex-col md:flex-row justify-between gap-3">
          <div className="text-gray-500 text-[11.5px]">
            © {new Date().getFullYear()} BLKPixel Technologies, LLC. All rights reserved.
          </div>
          <div className="flex gap-5 text-gray-500 text-[11.5px]">
            <Link to="/rapidreps-support" className="hover:text-white">RapidReps Support</Link>
            <Link to="/rapidreps-privacy" className="hover:text-white">Privacy Policy</Link>
            <button type="button" onClick={onGetStarted} className="hover:text-white bg-transparent">Contact</button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
